import { useState } from "react";
import type { KeyboardEvent } from "react";
import { closedLabel, HEALTH_LABEL, TONE_COLOR } from "../../lib/mkBoardView";
import { CHANNEL_COLOR, CHANNEL_LABEL } from "../../lib/mkRules";
import type { BoardGroup } from "../../types/contract";

interface GroupRowProps {
  group: BoardGroup;
  /** closed = 片付いたもの（淡色・理由表示）。 */
  variant: "running" | "upcoming" | "closed";
  onOpen: (initiativeId: string) => void;
}

/**
 * 進行中／これから／片付いたものの1グループ=1行（カードにしない・罫線区切りの行リスト）。
 * 行クリックで詳細。children があるときは「クーポンN種 ▸」で行内展開（展開ボタンは詳細遷移しない）。
 */
export function GroupRow({ group: g, variant, onOpen }: GroupRowProps) {
  const [expanded, setExpanded] = useState(false);
  const color = CHANNEL_COLOR[g.channel];
  const target = g.initiative_id;
  const interactive = target !== null;

  const open = () => {
    if (target) onOpen(target);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  };

  return (
    <div
      className={`mk2-row mk2-row-${variant}${interactive ? " mk2-row-link" : ""}`}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? open : undefined}
      onKeyDown={interactive ? onKeyDown : undefined}
    >
      <span className="mk2-chdot mk2-row-dot" style={{ background: color }} />
      <div className="mk2-row-main">
        <div className="mk2-row-head">
          <span className="mk2-row-ch" style={{ color }}>
            {CHANNEL_LABEL[g.channel]}
          </span>
          <span className="mk2-row-name">{g.name}</span>
          {/* 片付いた行に「要注意」等を出しても判断材料にならないので、health は進行中/これからだけ。 */}
          {g.health && variant !== "closed" && (
            <span className={`mk2-health mk2-health-${g.health}`}>{HEALTH_LABEL[g.health]}</span>
          )}
        </div>
        <div className="mk2-row-sub">
          {variant === "closed" ? (
            <>
              <span>{g.when_label}</span>
              <span className="mk2-sep">・</span>
              <span>{closedLabel(g)}</span>
            </>
          ) : (
            <>
              <span>{g.state_label}</span>
              <span className="mk2-sep">・</span>
              <span>{g.when_label}</span>
            </>
          )}
        </div>
        {g.metrics.length > 0 && (
          <div className="mk2-metrics">
            {g.metrics.map((m, i) => (
              <span className="mk2-metric" key={i}>
                <span className="mk2-metric-label">{m.label}</span>
                <span className="mk2-metric-value" style={m.tone ? { color: TONE_COLOR[m.tone] } : undefined}>
                  {m.value}
                </span>
              </span>
            ))}
          </div>
        )}
        {g.ai_note && <div className="mk2-row-note">{g.ai_note}</div>}
        {g.children.length > 0 && (
          <>
            <button
              type="button"
              className="mk2-children-btn"
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
            >
              クーポン{g.children.length}種 <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
            </button>
            {expanded && (
              <ul className="mk2-children">
                {g.children.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="mk2-child"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(c.id);
                      }}
                    >
                      <span className="mk2-child-name">{c.name}</span>
                      {c.note && <span className="mk2-child-note">{c.note}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
